/**
 * Vasim Hayat @ 1/24/2018
 */

import { Injectable } from '@angular/core';
import { DatePipe } from '@angular/common';
import { DomSanitizer } from '@angular/platform-browser';
import { sortArray } from '../util/array';
import {
  isNull,
  typeofObject,
  isEmptyObject,
  typeofArray,
  isString,
  isNotNull,
  typeofString,
  isEmptyString,
} from '../util/check';
import { FnDate } from '../util/types';
import { CURRENCY_REGEX } from '../constant/fn.constant';
import { FnDeviceConstant } from '../constant/fn-device.constant';

@Injectable()
export class FnUtilService {
  constructor(
    private dateFormatter: DatePipe,
    private sanitizer: DomSanitizer
  ) { }

  transform(
    value: any,
    format?: string,
    timezone?: string,
    locale?: string
  ): string | null {
    return this.dateFormatter.transform(value, format, timezone, locale);
  }

  compileHtml(htmlStr: any) {
    return this.sanitizer.bypassSecurityTrustHtml(htmlStr);
  }

  convertHtmlToString(htmlStr: any): string {
    return htmlStr ? String(htmlStr).replace(/<[^>]+>/gm, '') : '';
  }

  sortArray(objectArray: Array<any>, key: string, isDesc?: boolean) {
    return sortArray(objectArray, key, isDesc);
  }

  getWeek(busiDate: any) {
    const fDate = new Date(busiDate.getFullYear(), 0, 1);
    return Math.ceil(
      ((busiDate.getTime() - fDate.getTime()) / 86400000 + fDate.getDay() + 1) /
      7
    );
  }

  getDateByIndex(
    index: number,
    dateOb: FnDate,
    isFirstDay: boolean,
    isLastDay?: boolean
  ): Date {
    let date = 0;
    const _dateObj = new Date(dateOb);

    if (index > _dateObj.getDay()) {
      date = _dateObj.getDate() + index - _dateObj.getDay() - 7;
    } else {
      date = _dateObj.getDate() + (index - _dateObj.getDay());
    }

    if (isFirstDay) {
      _dateObj.setDate(date);
    } else if (isLastDay) {
      _dateObj.setDate(date + 7);
    }

    return _dateObj;
  }

  getWeekDiff(startIndex: number, startDate: FnDate, endDate: FnDate): number {
    const week1 = this.getDateByIndex(startIndex, startDate, true, false);
    const week2 = this.getDateByIndex(startIndex, endDate, true, false);
    const weekCount1 = this.getWeek(week1);
    let weekCount2 = this.getWeek(week2);
    if (weekCount2 < weekCount1) {
      weekCount2 += 52;
    }
    return weekCount2 - weekCount1 + 1;
  }

  getWeekArrayForDates(
    _startIndex: number,
    _startDate: FnDate,
    _endDate: FnDate
  ): Array<Date> {
    const noOfWeek = this.getWeekDiff(_startIndex, _startDate, _endDate);
    const newStartDate = this.getDateByIndex(
      _startIndex,
      _startDate,
      true,
      false
    );

    const weekArray: Array<any> = [];
    for (let i = 0; i < noOfWeek; i++) {
      const _weekDate = this.getDateByIndex(
        _startIndex,
        newStartDate,
        true,
        false
      );
      weekArray.push(this.dateFormatter.transform(_weekDate, 'MM/dd/yyyy'));
      newStartDate.setDate(newStartDate.getDate() + 7);
    }
    return weekArray;
  }

  getWeekArray(startDate: FnDate, noOfWeek: number): Array<Date> {
    const weekArray = [];
    const newStartDate = new Date(startDate);
    for (let i = 0; i < noOfWeek; i++) {
      const newWeekHash: any = {
        startDate: null,
        endDate: null,
        formattedEndDate: null,
      };
      newWeekHash.startDate = newStartDate;
      newWeekHash.endDate = new Date(
        newStartDate.setDate(newStartDate.getDate() + 6)
      );
      newWeekHash.formattedEndDate = this.dateFormatter.transform(
        newWeekHash.endDate,
        'yyyyMMdd'
      );
      weekArray.push(newWeekHash);
      newStartDate.setDate(newStartDate.getDate() + 1);
    }
    return weekArray;
  }

  weekHash(weekDayIndex: number, date: Date, langID: string) {
    if (weekDayIndex !== undefined && weekDayIndex < 7 && weekDayIndex >= 0) {
      let weekArray = [];
      let objectHash: any = {};
      let dateObjArray: any = [];
      dateObjArray = this.getWeekArrayOnIndex(weekDayIndex, date, false, false);
      for (let i = 0; i < 7; i++) {
        let dayHash: any = {}
        dayHash["day"] = dateObjArray[i].formatter("l", langID);
        dayHash["dateObj"] = dateObjArray[i];
        dayHash["index"] = dateObjArray[i].getDay();
        dayHash['formattedDateObj'] = dayHash["dateObj"].formatter("m/d/Y");
        weekArray.push(dayHash);
      }
      objectHash["weekArray"] = weekArray;
      objectHash["dateObj"] = this.getWeekArrayOnIndex(weekDayIndex, date, true, false);
      return objectHash;
    }
    return undefined;
  }

  getWeekArrayOnIndex(index: number, dateOb: string | Date, isFirstDay: boolean, isLastDay: boolean) {
    const dateObjArray = [];
    let date = 0;
    const dateObj = new Date(dateOb);
    if (index > dateObj.getDay()) {
      date = dateObj.getDate() + index - dateObj.getDay() - 7;
    } else {
      date = dateObj.getDate() + (index - dateObj.getDay());
    }
    if (isFirstDay) {
      const dateObject = new Date(dateOb);
      dateObject.setDate(date);
      return dateObject;
    } else if (isLastDay) {
      const dateObject = new Date(dateOb);
      dateObject.setDate(date + 7);
      return dateObject;
    }
    for (let i = 0; i < 7; i++) {
      const dateObject = new Date(dateOb);

      dateObject.setDate(date);
      dateObjArray.push(dateObject);
      date++;
    }
    return dateObjArray;
  }

  currencyFormatter(val: any, currencyString?: any) {
    val = this.reverseString(val);
    let result = '';
    for (let i = 0; i < val.length; i++) {
      if (i % 3 === 0 && i !== 0) {
        result += ',' + val.charAt(i);
      } else {
        result += val.charAt(i);
      }
    }
    return this.reverseString(result).replace(',.', '.');
  }

  currencyRgxFormat(n: number, currency: any, fixedDecimal: any) {

    return currency + ' ' + n.toFixed(fixedDecimal).replace(CURRENCY_REGEX, '$1,');
    // currencyVal = currencyVal.replaceAll(" ", "");

  }

  // converting rgb value to hex value
  rgb2hex = (rgb: string) => {
    const _itemList = rgb.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);
    return (
      '#' +
      ('0' + parseInt(_itemList[1], 10).toString(16)).slice(-2) +
      ('0' + parseInt(_itemList[2], 10).toString(16)).slice(-2) +
      ('0' + parseInt(_itemList[3], 10).toString(16)).slice(-2)
    );
  };

  isEmptyObject = (_value: any) => {
    if (isNull(_value)) {
      return true;
    } else if (typeofArray(_value)) {
      return _value.length === 0;
    } else if (typeofString(_value)) {
      return isEmptyString(_value);
    } else if (typeofObject(_value)) {
      return isEmptyObject(_value);
    }
    return false;
  };

  sortOrderingComparator = (fields: Array<string>, isIgnoreCase?: boolean) => {
    return (a: object | any, b: object | any) => {
      return fields
        .map((obj: any) => {
          let dir = 1;
          if (obj[0] === '-') {
            dir = -1;
            obj = obj.substring(1);
          }
          let _tempVal1 = a[obj];
          let _tempVal2 = b[obj];
          if (isIgnoreCase && isString(_tempVal1) && isString(_tempVal2)) {
            _tempVal1 = _tempVal1.toLowerCase();
            _tempVal2 = _tempVal2.toLowerCase();
          }
          if (_tempVal1 > _tempVal2) {
            return dir;
          }
          if (_tempVal1 < _tempVal2) {
            return -dir;
          }
          return 0;
        })
        .reduce((p, n) => {
          return p ? p : n;
        }, 0);
    };
  };

  sortObjectOnKeys = (
    objArray: Array<object>,
    sortKeyStr: string,
    isIgnoreCase?: boolean
  ) => {
    if (
      !this.isEmptyObject(sortKeyStr) &&
      !this.isEmptyObject(objArray) &&
      Array.isArray(objArray)
    ) {
      // @ts-ignore
      return objArray.sort(
        this.sortOrderingComparator(sortKeyStr.split('~'), isIgnoreCase)
      );
    } else {
      return objArray;
    }
  };

  toStringVal = (objVal: object) => objVal + '';
  reverseString = (strVal: string) => strVal.split('').reverse().join('');
  reverse = (objVal: object) => {
    if (isString(objVal)) {
      return this.reverseString(this.toStringVal(objVal));
    } else {
      return new Array(objVal).reverse();
    }
  };

  getSumOnKey = (objArray: Array<object>, key: string) => {
    let sum = 0;
    objArray.forEach((value: any, index) => {
      sum += parseFloat(value[key]);
    });
    return sum;
  };

  mapFromArray = <T>(_array: Array<any>, _key: string, isLowercase?: boolean) => {
    if (isNull(_array)) {
      return { keyName: new Array<T>() };
    }

    const _map: any = {};
    for (let i = 0; i < _array.length; i++) {
      if (isNotNull(_array[i][_key])) {
        if (isLowercase) {
          _map[_array[i][_key].toLowerCase()] = _array[i];
        } else {
          _map[_array[i][_key]] = _array[i];
        }
      }
    }
    return _map;
  };

  mapFromArrayOnInnerKey = (
    objArray: Array<any>,
    outerKey: string,
    innerKey: string
  ) => {
    const _returnHash: any = {};
    objArray.forEach((value, index) => {
      if (!isNull(value[outerKey]) && isNull(value[outerKey][innerKey])) {
        _returnHash[value[outerKey][innerKey]] = value;
      }
    });
    return _returnHash;
  };

  mapFromTwoLevelArray = (
    objArray: Array<any>,
    relArrayKey: string,
    relKey: string
  ) => {
    const returnHash: any = {};
    objArray.forEach((value, index) => {
      if (isNotNull(value) && isNotNull(value[relArrayKey])) {
        value[relArrayKey].forEach((childVal: any) => {
          if (isNotNull(childVal[relKey])) {
            returnHash[childVal[relKey]] = childVal;
          }
        });
      }
    });
    return returnHash;
  };

  mapFromTwoLevelHash = (
    objArray: Array<object>,
    keyName: string,
    relArraykey: string,
    relKey: string
  ) => {
    const returnHash: any = {};
    objArray.forEach((value: any, index) => {
      if (isNotNull(value)) {
        // tslint:disable-next-line:forin
        for (const i in value) {
          for (const j in value[i][relArraykey]) {
            if (isNotNull(value[i][relArraykey][j][relKey])) {
              returnHash[value[i][relArraykey][j][relKey]] =
                value[i][relArraykey][j];
            }
          }
        }
      }
    });
    return returnHash;
  };

  mapFromArrayWithRel = (
    objArray: Array<object>,
    key: string,
    relArrayKey: string,
    relKey: string
  ) => {
    objArray.forEach((value: any, index) => {
      if (isNotNull(value[relArrayKey])) {
        value[relArrayKey + 'Map'] = this.mapFromArray(
          value[relArrayKey],
          relKey
        );
      }
    });
    return this.mapFromArray(objArray, key);
  };

  // Will return the string value on objects values
  getStringOfObjectOnKey = (
    objectVal: object | any,
    skipKey?: boolean,
    keyArray?: Array<string>
  ) => {
    skipKey = skipKey || false;
    const _keyArray = skipKey ? [] : keyArray;
    let _returnStr = '';
    for (const key in objectVal) {
      if (!skipKey && !_keyArray.includes(key.toString())) {
        continue;
      } else {
        _returnStr += ' ' + objectVal[key];
      }
    }
    return _returnStr;
  };

  filterArray = (
    inputArray: object[],
    searchTerm: string,
    skipFilterKey?: boolean,
    filterKeyStr: string = ''
  ) => {
    const _filterArray = new Array<object>();
    const _serchTerm = this.getRegStr(searchTerm.toLocaleLowerCase());
    const _keyArray = filterKeyStr.split('~');
    for (let _i = 0; _i < inputArray.length; _i++) {
      const _item = inputArray[_i];
      const _objectValStr = this.getStringOfObjectOnKey(
        _item,
        skipFilterKey,
        _keyArray
      );
      if (_objectValStr.toLocaleLowerCase().match(_serchTerm)) {
        _filterArray.push(_item);
      }
    }
    return _filterArray;
  };

  getRegStr(str: string) {
    let tempStr = '';
    const specialChars = '\\/+()[]{}';
    for (let i = 0; i < str.length; i++) {
      const char = str[i];
      if (specialChars.includes(char)) {
        tempStr += '\\' + char;
      } else {
        tempStr += char;
      }
    }
    return tempStr;
  }


  replaceAll(
    stringObj: string,
    inputStr: string,
    outPutStr: string,
    ignore?: boolean
  ): string {
    return stringObj.replace(
      new RegExp(
        inputStr.replace(
          /([\/\,\!\\\^\$\{\}\[\]\(\)\.\*\+\?\|\<\>\-\&])/g,
          '\\$&'
        ),
        ignore ? 'gi' : 'g'
      ),
      typeof outPutStr === 'string'
        ? outPutStr.replace(/\$/g, '$$$$')
        : outPutStr
    );
  }

  mapFromArrayOfArrayObjects(
    object: object | any,
    arrayObject: Array<object | any>,
    keyName: string,
    arrayName: string
  ) {
    // 'id', 'menuItemArray' this.menuDataHash, this.menuData.menuItemArray
    if (arrayObject) {
      for (let i = 0; i < arrayObject.length; i++) {
        if (isNotNull(arrayObject[i][keyName])) {
          object[arrayObject[i][keyName]] = arrayObject[i];
          if (arrayName != null) {
            this.mapFromArrayOfArrayObjects(
              object,
              arrayObject[i][arrayName],
              keyName,
              arrayName
            );
          }
        }
      }
    }
  }

  copy(object: object) {
    return this.isEmptyObject(object)
      ? object
      : JSON.parse(JSON.stringify(object));
  }

  toBoolean(bolStr: any): boolean {
    if (
      isNotNull(bolStr) &&
      (bolStr === true || bolStr === 'true' || bolStr === 1)
    ) {
      return true;
    }
    return false;
  }

  arrayFromMap(objectMap: object | any) {
    const returnArray = [];
    for (const key in objectMap) {
      if (objectMap.hasOwnProperty(key)) {
        returnArray.push(objectMap[key]);
      }
    }
    return returnArray;
  }

  arrayFromArrayOfMap(objectArray: Array<object | any>, key: string) {
    const returnArray = [];
    for (let i = 0; i < objectArray.length; i++) {
      if (isNotNull(objectArray[i][key])) {
        returnArray.push(objectArray[i][key]);
      }
    }
    return returnArray;
  }

  singleKeyArrayFromMap(arrayObject: Array<object | any>, keyName: string) {
    const arrayList = [];
    for (let i = 0; i < arrayObject.length; i++) {
      if (isNotNull(arrayObject[i][keyName])) {
        arrayList.push(arrayObject[i][keyName]);
      }
    }

    return arrayList;
  }

  altaFormatter(val: any, prefix: any, decimalValStrength: any, suffix: any) {
    const originalVal = val;

    function responseObject(
      _val: any,
      formatCall: boolean,
      noFormat: boolean,
      originalval: any
    ) {
      const _esp: any = {
        value: '',
        oldvalue: '',
        formatcalling: null,
        noformat: null,
      };
      _esp.value = _val;
      _esp.oldvalue = originalval;
      _esp.formatcalling = formatCall;
      _esp.noformat = noFormat;
      return _esp;
    }

    let callingStatus = true;
    let noStatus = true; // if its true number is in right format else number is in wrong format
    if (isNull(prefix) || isNaN(prefix) === false) {
      callingStatus = false;
      prefix = '';
    }
    if (isNull(suffix) || isNaN(suffix) === false) {
      callingStatus = false;
      suffix = '';
    }
    if (
      isNull(decimalValStrength) ||
      decimalValStrength === '' ||
      isNaN(decimalValStrength)
    ) {
      callingStatus = false;
      decimalValStrength = 0;
    }

    if (isNotNull(val) && !isNaN(val)) {
      return responseObject(
        this.currencyRgxFormat(parseFloat(val), prefix, decimalValStrength) +
        '' +
        suffix,
        callingStatus,
        noStatus,
        val
      );
    } else {
      noStatus = false;
      return responseObject(val, callingStatus, noStatus, originalVal);
    }
  }

  format(value: any, IID: string, countryFormat?: string) {
    if (isNull(countryFormat)) {
      countryFormat = '$';
    }
    if (IID === '$') {
      return this.altaFormatter(value, countryFormat, 0, '');
    } else if (IID === 'N') {
      return this.altaFormatter(value, '', 0, '');
    } else if (IID === '$1D') {
      return this.altaFormatter(value, countryFormat, 1, '');
    } else if (IID === '$2D') {
      return this.altaFormatter(value, countryFormat, 2, '');
    } else if (IID === '$3D') {
      return this.altaFormatter(value, countryFormat, 3, '');
    } else if (IID === '$4D') {
      return this.altaFormatter(value, countryFormat, 4, '');
    } else if (IID === '1D') {
      return this.altaFormatter(value, '', 1, '');
    } else if (IID === '2D') {
      return this.altaFormatter(value, '', 2, '');
    } else if (IID === '3D') {
      return this.altaFormatter(value, '', 3, '');
    } else if (IID === '%') {
      return this.altaFormatter(value, '', 2, '%');
    }
  }

  mergeHash(destination: any, src: any) {
    for (const key in src) {
      if (Array.isArray(src[key])) {
        if (isNull(destination[key])) {
          destination[key] = [];
        }
        destination[key] = destination[key].concat(src[key]);
      } else {
        destination[key] = src[key];
      }
    } // end of for loop
    return destination;
  }

  getSumOnTheBasisOfKey(arrayObject: any, key1: any, key2: any) {
    let sum = 0;
    for (let i = 0; i < arrayObject.length; i++) {
      sum += parseFloat(arrayObject[i][key1][key2]);
    }
    return sum;
  }

  getSimpleHash(arrayOfObject: any) {
    const _returnObj: any = {};
    arrayOfObject.forEach((obj: any) => {
      _returnObj[obj] = obj;
    });
    return _returnObj;
  }

  countOccurence(stringvalue: any, charvalue: string) {
    return stringvalue.match(new RegExp(charvalue, 'g') || []).length;
  }

  replaceDotToUnderscore(value: any) {
    if (value.indexOf('.') !== -1) {
      value = value.replaceAll('.', '_');
    }
    return value;
  }

  checkCollectionLength(obj: any) {
    if (this.isEmptyObject(obj)) {
      return 0;
    }

    if (typeofObject(obj)) {
      return Object.keys(obj).length;
    } else if (Array.isArray(obj)) {
      return obj.length;
    }
    return obj.length;
  }

  hashFromArrayWithRd(arrayObjects: Array<any>, keyName: string) {
    const _returnHash: any = {};
    for (let i = 0; i < arrayObjects.length; i++) {
      const key = arrayObjects[i][keyName];
      if (isNotNull(_returnHash[key])) {
        _returnHash[key].push(arrayObjects[i]);
      } else {
        _returnHash[key] = [];
        _returnHash[key].push(arrayObjects[i]);
      }
    }
    return _returnHash;
  }
  
  formatShiftTime(time: any, shift: any) {
    let timeArr = time.split(' - ');
    if (timeArr.length > 1) {
      timeArr = time.split(' -');
    }
    if (timeArr.length > 1) {
      timeArr = time.split('-');
    }
    if (timeArr.length > 1) {
      timeArr = time.split('- ');
    }
    const startTime = timeArr[0].trim().substr(0, timeArr[0].trim().length - 2);
    const endTime = timeArr[1].trim().substr(0, timeArr[1].trim().length - 2);
    const startTimeArr = startTime.split(':');
    if (startTimeArr.length > 1) {
      // tslint:disable-next-line:radix
      shift.startTime =
        // tslint:disable-next-line:radix
        parseInt(startTimeArr[0]) * 60 + parseInt(startTimeArr[1]);
    } else {
      // tslint:disable-next-line:radix
      shift.startTime = parseInt(startTimeArr[0]) * 60;
    }
    const endTimeArr = endTime.split(':');
    if (endTimeArr.length > 1) {
      // tslint:disable-next-line:radix
      shift.endTime = parseInt(endTimeArr[0]) * 60 + parseInt(endTimeArr[1]);
    } else {
      // tslint:disable-next-line:radix
      shift.endTime = parseInt(endTimeArr[0]) * 60;
    }
  }

  getIndexWithTwoKeys(
    array: Array<any>,
    firstKey: string,
    firstValue: any,
    secondKey: string,
    secondVal: any
  ) {
    for (let i = 0; i < array.length; i++) {
      if (
        array[i][firstKey] === firstValue &&
        array[i][secondKey] === secondVal
      ) {
        return i;
      }
    }
    return -1;
  }

  getIndexIfObjWithAttr(array: Array<any>, attr: any, value: any) {
    for (let i = 0; i < array.length; i++) {
      if (array[i][attr] === value) {
        return i;
      }
    }
    return -1;
  }

  isValidObjects(obj1: any, obj2: any) {
    return isNull(obj1) && isNull(obj2);
  }

  isSameObjects(hashObject1: any, hashObject2: any) {
    let flag = true;

    if (
      this.checkCollectionLength(hashObject1) > 0 &&
      this.checkCollectionLength(hashObject2) > 0
    ) {
      for (const key in hashObject1) {
        if (
          !this.isValidObjects(hashObject1[key], hashObject2[key]) &&
          hashObject1[key] !== hashObject2[key]
        ) {
          flag = false;
          break;
        }
      }
    } else {
      flag = false;
    }
    return flag;
  }

  copyObject(obj: any) {
    return this.isEmptyObject(obj) ? obj : JSON.parse(JSON.stringify(obj));
  }

  objectEquals(x: any, y: any): boolean {
    'use strict';

    if (x === null || x === undefined || y === null || y === undefined) {
      return x === y;
    }
    // after this just checking type of one would be enough
    if (x.constructor !== y.constructor) {
      return false;
    }
    // if they are functions, they should exactly refer to same one (because of closures)
    if (x instanceof Function) {
      return x === y;
    }
    // if they are regexps, they should exactly refer to same one (it is hard to better equality check on current ES)
    if (x instanceof RegExp) {
      return x === y;
    }
    if (x === y || x.valueOf() === y.valueOf()) {
      return true;
    }
    if (Array.isArray(x) && x.length !== y.length) {
      return false;
    }

    // if they are dates, they must had equal valueOf
    if (x instanceof Date) {
      return false;
    }

    // if they are strictly equal, they both need to be object at least
    if (!(x instanceof Object)) {
      return false;
    }
    if (!(y instanceof Object)) {
      return false;
    }

    // recursive object equality check
    const p = Object.keys(x);
    return (
      Object.keys(y).every((i) => {
        return p.indexOf(i) !== -1;
      }) &&
      p.every((i) => {
        return this.objectEquals(x[i], y[i]);
      })
    );
  }

  dummyArrayOfLength(length: number) {
    const res = [];
    for (let i = 0; i < length; i++) {
      res.push(i + 1);
    }

    return res;
  }

  getCleanXML(str: string) {
    if (isNull(str)) {
      return '';
    }
    if (str.indexOf('&') === -1 && str.indexOf('<') === -1) {
      return str;
    }
    if (str === '&nbsp;') {
      return str;
    }
    let strBuf = '';
    const charArray = str.split('');
    for (let i = 0; i < charArray.length; i++) {
      if (charArray[i] === '&') {
        strBuf += '&amp;';
      } else if (charArray[i] === '<') {
        strBuf += '&lt;';
      } else if (charArray[i] === '>') {
        strBuf += '&gt;';
      } else {
        strBuf += charArray[i];
      }
    }
    return strBuf.toString();
  }

  replaceStringAt(
    sourceStr: string,
    sIndex: number,
    endIndex: number,
    toString: any
  ) {
    let _returnStr = '';
    _returnStr += sourceStr.substring(0, sIndex);
    _returnStr += toString;
    _returnStr += sourceStr.substring(endIndex, sourceStr.length);
    return _returnStr;
  }

  mergeObject(destObj: any, sourceObj: any) {
    for (const key in sourceObj) {
      if (!this.isEmptyObject(sourceObj[key])) {
        destObj[key] = sourceObj[key];
      }
    }
    return destObj;
  }

  isNumberStr(numStr: any) {
    numStr = numStr.trim(' ');
    return this.isEmptyObject(numStr)
      ? false
      : numStr.match(/^\s*(\-|\+)?(\d+|(\d*(\.\d*)))\s*$/);
  }

  isIntegerNumber() {
    return new RegExp(/^[0-9]*$/);
  }

  getSpecialCharRegex() {
    return new RegExp(/^(?=.*[/\\!(){}\-_~,:;+=<>@#?\$%\^&\*\]\[])/);
  }

  getNumberRegex() {
    return new RegExp(/^(?=.*[0-9])/);
  }

  getPasswordRegex() {
    return new RegExp(/^(?=.{8,})(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[/\\!(){}\-_~,:;+=<>@#?\$%\^&\*\]\[])(^\S*$).*/);
  }

  getCharRegex() {
    return new RegExp(/^(?=.*[a-z])(?=.*[A-Z])/);
  }

  stringSortArray(arrayObject: any, keyName: string, isReverse?: boolean) {
    arrayObject.sort((object1: any, object2: any) => {
      if (isReverse) {
        return object2[keyName].localeCompare(object1[keyName]);
      } else {
        return object1[keyName].localeCompare(object2[keyName]);
      }
    });
    return arrayObject;
  }

  setDifference(array1: Array<any>, array2: Array<any>, key: string) {
    const tempArray = new Array<any>();
    for (let index = 0; index < array1.length; index++) {
      const value = array1[index][key];
      const anotherArray = array2.filter((dataObj: any) => {
        return dataObj[key] === value;
      });
      if (anotherArray.length === 0) {
        tempArray.push(array1[index]);
      }
    }
    return tempArray;
  }

  inetersection(array1: Array<any>, array2: Array<any>, key: string) {
    const tempArray = new Array<any>();
    for (let index = 0; index < array1.length; index++) {
      const value = array1[index][key];
      const anotherArray = array2.filter((dataObj: any) => {
        return dataObj[key] === value;
      });
      if (anotherArray.length > 0) {
        tempArray.push(array1[index]);
      }
    }
    return tempArray;
  }

  getBoolValue(bool: any) {
    if (isNotNull(bool) && (bool === true || bool === 'true' || bool === 1)) {
      return true;
    }
    return false;
  }

  syncLoop(iterations: number, doProcess: any, exit: any) {
    let index = 0;
    let done = false;
    let shouldExit = false;
    const loopObject = {
      next() {
        if (done) {
          if (shouldExit && exit) {
            return exit(); // Exit the loop
          }
        }
        // do iteation
        if (index < iterations) {
          index++; // increase Index
          doProcess(loopObject); // Run our process, pass in the loop
        } else {
          done = true; // Make sure we say we're done
          if (exit) {
            exit();
          } // Call the callback on exit
        }
      },
      iteration() {
        return index - 1; // Return the current index count
      },
      break(end: boolean) {
        done = true; // End the loop
        shouldExit = end; // Passing end as true means we still call the exit callback
      },
    };
    loopObject.next(); // run for first time...
    return loopObject;
  }

  jsonArrayFromCsvData(csvObjArray: Array<any>) {
    let tempArray: Array<any> = [];
    let headers = (<string>csvObjArray[0]).split(',');
    for (let i = 1; i < csvObjArray.length; i++) {
      if (this.isEmptyObject(csvObjArray[i])) {
        continue;
      }
      let csvObj = (<string>csvObjArray[i]).split(',');
      let tempObj: any = {};
      for (let j = 0; j < headers.length; j++) {
        tempObj[headers[j]] = csvObj[j];
      }
      tempArray.push(tempObj);
    }
    return tempArray;
  }

  deviceIcon(deviceType: string) {
    if (isNull(deviceType) || isEmptyString(deviceType)) {
      return (
        FnDeviceConstant.DEVICE_FA_ICONS.MOBILE +
        ' ' +
        FnDeviceConstant.DEVICE_COLOR_MAP.ANDROID // need add new icon for mobile
      );
    }
    const icons: any = FnDeviceConstant.DEVICE_FA_ICONS;
    const colorMap: any = FnDeviceConstant.DEVICE_COLOR_MAP;
    return icons[deviceType] + ' ' + colorMap[deviceType];
  }

  getDurationFromTime(timeStr: any): string {
    timeStr = parseInt(timeStr);
    if (timeStr < 24 * 60) {
      const hrs = timeStr / 60;
      const mins = timeStr % 60;
      return (hrs >= 1 ? (Math.floor(hrs) + ' hrs') : '') + ((mins < 60 && mins > 0) ? (' ' + mins + ' mins ago') : (hrs >= 1 ? ' ago' : ' '));
    } else {
      const day = timeStr / (24 * 60);
      const hours = (timeStr % (24 * 60)) / 60;
      return Math.round(day) + ' day(s)' + ((hours < 24 && hours > 0) ? (' ' + Math.round(hours) + ' hrs ago') : ' ago');
    }
  }

  toUSFormat(phn: string | number): string | any {
    if (typeof phn === 'string' && phn.length >= 10) {
      return this.formatPhone(phn);
    } else if (typeof phn === 'number') {
      const phnStr = String(phn);
      if (phnStr.length >= 10) {
        return this.formatPhone(phnStr);
      } else if (phnStr.length < 10) {
        const newVal = this.prependValues(phnStr, '0', 10 - phnStr.length);
        return this.formatPhone(newVal);
      }
    }
    return phn; // Return as is if it doesn't match any conditions
  }

  prependValues(objValue: string | number, prependValue: string, noOfTimes: number): string | any {
    let tempStr!: string;
    // Convert objValue to string
    if (typeof objValue === 'number' || typeof objValue === 'string') {
      tempStr = String(objValue);
    } else {
      return objValue; // Return as is if neither string nor number
    }
    // Prepend the specified value if noOfTimes is a number
    if (typeof noOfTimes === 'number') {
      for (let times = 0; times < noOfTimes; times++) {
        tempStr = prependValue + tempStr;
      }
      return tempStr;
    }
    return objValue; // Return original value if noOfTimes is not a number
  }

  private formatPhone(phn: string): string {
    return `${phn.substring(0, 3)}-${phn.substring(3, 3)}-${phn.substring(6)}`;
  }

  phoneFormat(mask: string, number: string | number): string | null {
    if (this.isEmptyObject(number)) {
      return null;
    }
    if (typeof number === 'number') {
      number = String(number);
    }
    const actualCount = mask.split('X').length - 1;
    const s = String(number);
    let r = '';
    let is = 0;
    if (s.length >= actualCount) {
      for (let im = 0; im < mask.length && is < s.length; im++) {
        r += mask.charAt(im) === 'X' ? s.charAt(is++) : mask.charAt(im);
      }
      r += s.substring(is);
      return r;
    }
    return s;
  }

}
